import axios, { AxiosInstance } from "axios";
import {
  GitLabProject,
  GitLabTag,
  GitLabRelease,
  GitLabBranch,
  ProjectVersionInfo,
  ProjectTechnology,
  ReleaseResult,
  GitLabPipeline,
  GitLabMergeRequest,
} from "../types/gitlab";

class GitLabService {
  private api: AxiosInstance | null = null;
  private accessToken: string = "";

  configure(gitlabUrl: string, accessToken: string) {
    this.accessToken = accessToken;
    // Use separate proxy server to avoid CORS issues
    this.api = axios.create({
      baseURL: "/api/gitlab",
      headers: {
        "PRIVATE-TOKEN": accessToken,
        "Content-Type": "application/json",
      },
    });
  }

  isConfigured(): boolean {
    return this.api !== null;
  }

  async getProjects(
    search?: string,
    page: number = 1,
    perPage: number = 100
  ): Promise<GitLabProject[]> {
    if (!this.api) throw new Error("GitLab service not configured");

    const params: Record<string, string | number | boolean> = {
      per_page: perPage,
      page,
      order_by: "last_activity_at",
      sort: "desc",
      include_subgroups: true,
    };

    if (search) {
      params.search = search;
    }

    // Fetch projects from the kannact group
    const response = await this.api.get<GitLabProject[]>(
      "/groups/kannact/projects",
      {
        params,
      }
    );
    return response.data;
  }

  async getAllProjects(): Promise<GitLabProject[]> {
    if (!this.api) throw new Error("GitLab service not configured");

    const allProjects: GitLabProject[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const projects = await this.getProjects(undefined, page, 100);
      allProjects.push(...projects);
      hasMore = projects.length === 100;
      page++;
    }

    // Filter out infrastructure projects, products projects, templates, release-creator, and projects not in a subgroup
    return allProjects.filter((project) => {
      const path = project.path_with_namespace.toLowerCase();
      const name = project.name.toLowerCase();

      // Check if project is in a subgroup (path has more than 2 parts: group/subgroup/project)
      const pathParts = path.split("/");
      const isInSubgroup = pathParts.length >= 3;

      return (
        isInSubgroup &&
        !path.includes("infrastructure") &&
        !path.includes("/templates/") &&
        !name.includes("infrastructure") &&
        !name.includes("-products") &&
        !name.includes("release-creator")
      );
    });
  }

  async createTag(
    projectId: number,
    tagName: string,
    ref: string,
    message?: string
  ): Promise<GitLabTag> {
    if (!this.api) throw new Error("GitLab service not configured");

    const response = await this.api.post<GitLabTag>(
      `/projects/${projectId}/repository/tags`,
      {
        tag_name: tagName,
        ref,
        message: message || `Release ${tagName}`,
      }
    );

    return response.data;
  }

  async createRelease(
    projectId: number,
    tagName: string,
    name: string,
    description?: string
  ): Promise<GitLabRelease> {
    if (!this.api) throw new Error("GitLab service not configured");

    const response = await this.api.post<GitLabRelease>(
      `/projects/${projectId}/releases`,
      {
        tag_name: tagName,
        name,
        description: description || `Release ${name}`,
      }
    );

    return response.data;
  }

  async deleteTag(projectId: number, tagName: string): Promise<boolean> {
    if (!this.api) throw new Error("GitLab service not configured");

    try {
      await this.api.delete(
        `/projects/${projectId}/repository/tags/${encodeURIComponent(tagName)}`
      );
      return true;
    } catch {
      return false;
    }
  }

  async deleteRelease(projectId: number, tagName: string): Promise<boolean> {
    if (!this.api) throw new Error("GitLab service not configured");

    try {
      await this.api.delete(
        `/projects/${projectId}/releases/${encodeURIComponent(tagName)}`
      );
      return true;
    } catch {
      return false;
    }
  }

  async forceCreateTagAndRelease(
    project: GitLabProject,
    releaseName: string,
    description?: string
  ): Promise<ReleaseResult> {
    try {
      // First, try to delete existing release and tag
      await this.deleteRelease(project.id, releaseName);
      await this.deleteTag(project.id, releaseName);

      // Small delay to ensure GitLab processes the deletion
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Now create the new tag
      const tag = await this.createTag(
        project.id,
        releaseName,
        project.default_branch,
        `Tag for release ${releaseName}`
      );

      // Create release for the tag
      const release = await this.createRelease(
        project.id,
        releaseName,
        releaseName,
        description || `Release ${releaseName} created via Release Creator`
      );

      return {
        project,
        success: true,
        tag,
        release,
        releaseType: "tag",
      };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || "Unknown error";

      return {
        project,
        success: false,
        error: `Force replace failed: ${errorMessage}`,
      };
    }
  }

  async createTagAndRelease(
    project: GitLabProject,
    releaseName: string,
    description?: string
  ): Promise<ReleaseResult> {
    try {
      // Create tag on default branch
      const tag = await this.createTag(
        project.id,
        releaseName,
        project.default_branch,
        `Tag for release ${releaseName}`
      );

      // Create release for the tag
      const release = await this.createRelease(
        project.id,
        releaseName,
        releaseName,
        description || `Release ${releaseName} created via Release Creator`
      );

      return {
        project,
        success: true,
        tag,
        release,
      };
    } catch (error: any) {
      let errorMessage =
        error.response?.data?.message || error.message || "Unknown error";

      // Make error messages more user-friendly
      if (
        errorMessage.includes("Target") &&
        errorMessage.includes("is invalid")
      ) {
        errorMessage = `Branch "${project.default_branch}" not found - repository may be empty`;
      } else if (errorMessage.includes("already exists")) {
        errorMessage = `Tag "${releaseName}" already exists`;
      }

      return {
        project,
        success: false,
        error: errorMessage,
      };
    }
  }

  async getProjectBranches(projectId: number): Promise<GitLabBranch[]> {
    if (!this.api) throw new Error("GitLab service not configured");

    try {
      const response = await this.api.get<GitLabBranch[]>(
        `/projects/${projectId}/repository/branches`,
        { params: { per_page: 100 } }
      );
      return response.data;
    } catch {
      return [];
    }
  }

  async getProjectTags(
    projectId: number,
    perPage: number = 1
  ): Promise<GitLabTag[]> {
    if (!this.api) throw new Error("GitLab service not configured");

    try {
      const response = await this.api.get<GitLabTag[]>(
        `/projects/${projectId}/repository/tags`,
        { params: { per_page: perPage, order_by: "updated", sort: "desc" } }
      );
      return response.data;
    } catch {
      return [];
    }
  }

  async getBranchCommit(
    projectId: number,
    branchName: string
  ): Promise<GitLabBranch | null> {
    if (!this.api) throw new Error("GitLab service not configured");

    try {
      const response = await this.api.get<GitLabBranch>(
        `/projects/${projectId}/repository/branches/${encodeURIComponent(
          branchName
        )}`
      );
      return response.data;
    } catch {
      return null;
    }
  }

  async getProjectVersionInfo(
    project: GitLabProject
  ): Promise<ProjectVersionInfo> {
    if (!this.api) throw new Error("GitLab service not configured");

    try {
      // Check if UAT branch exists
      const uatBranch = await this.getBranchCommit(project.id, "uat");

      if (uatBranch) {
        // UAT branch exists - get the last commit from main/default branch
        const mainBranch = await this.getBranchCommit(
          project.id,
          project.default_branch
        );
        if (mainBranch) {
          return {
            type: "commit",
            value: mainBranch.commit.title,
            date: mainBranch.commit.committed_date,
            hasUatBranch: true,
          };
        }
      }

      // No UAT branch - get the last tag
      const tags = await this.getProjectTags(project.id, 1);
      if (tags.length > 0) {
        return {
          type: "tag",
          value: tags[0].name,
          date: tags[0].commit.authored_date,
          hasUatBranch: false,
        };
      }

      // No tags found
      return {
        type: "tag",
        value: "No releases",
        date: "",
        hasUatBranch: false,
      };
    } catch (error) {
      console.error(`Error fetching version info for ${project.name}:`, error);
      return {
        type: "tag",
        value: "Error",
        date: "",
        hasUatBranch: false,
      };
    }
  }

  async detectProjectTechnology(
    projectId: number,
    defaultBranch: string
  ): Promise<ProjectTechnology> {
    if (!this.api) return "Unknown";

    try {
      // Check for common framework files in the repository root
      const response = await this.api.get<
        Array<{ name: string; type: string }>
      >(`/projects/${projectId}/repository/tree`, {
        params: { ref: defaultBranch, per_page: 50 },
      });

      const files = response.data.map((f) => f.name.toLowerCase());

      // Check for Next.js (must check before React since Next.js also has package.json)
      if (
        files.includes("next.config.js") ||
        files.includes("next.config.mjs") ||
        files.includes("next.config.ts")
      ) {
        return "Next.js";
      }

      // Check for Python
      if (
        files.includes("requirements.txt") ||
        files.includes("pyproject.toml") ||
        files.includes("setup.py") ||
        files.includes("pipfile")
      ) {
        return "Python";
      }

      // Check for Java
      if (
        files.includes("pom.xml") ||
        files.includes("build.gradle") ||
        files.includes("build.gradle.kts")
      ) {
        return "Java";
      }

      // Check for Node.js/React/Next.js (package.json exists)
      if (files.includes("package.json")) {
        try {
          const packageResponse = await this.api.get<{ content: string }>(
            `/projects/${projectId}/repository/files/package.json`,
            { params: { ref: defaultBranch } }
          );
          const content = atob(packageResponse.data.content);
          if (content.includes('"next"') || content.includes("'next'")) {
            return "Next.js";
          }
          if (content.includes('"react"') || content.includes("'react'")) {
            return "React";
          }
          // Has package.json but no React/Next - it's a Node.js project
          return "Node.js";
        } catch {
          // If we can't read package.json, assume it's a Node.js project
          return "Node.js";
        }
      }

      return "Unknown";
    } catch {
      return "Unknown";
    }
  }

  async enrichProjectsWithVersionInfo(
    projects: GitLabProject[],
    onProgress?: (enrichedProjects: GitLabProject[]) => void
  ): Promise<GitLabProject[]> {
    const enrichedProjects = [...projects];
    const batchSize = 5; // Process 5 projects at a time to avoid rate limiting

    for (let i = 0; i < projects.length; i += batchSize) {
      const batch = projects.slice(i, i + batchSize);

      // Process batch in parallel - fetch both version info and technology
      const results = await Promise.all(
        batch.map(async (project, batchIndex) => {
          const [versionInfo, technology] = await Promise.all([
            this.getProjectVersionInfo(project),
            this.detectProjectTechnology(project.id, project.default_branch),
          ]);
          return { index: i + batchIndex, versionInfo, technology };
        })
      );

      // Update enriched projects with results
      for (const { index, versionInfo, technology } of results) {
        enrichedProjects[index] = {
          ...enrichedProjects[index],
          versionInfo,
          technology,
        };
      }

      // Notify progress callback so UI updates incrementally
      if (onProgress) {
        onProgress([...enrichedProjects]);
      }

      // Small delay between batches to avoid rate limiting
      if (i + batchSize < projects.length) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    return enrichedProjects;
  }

  // ===== Pipeline and MR Monitoring Methods =====

  async getTagPipeline(
    projectId: number,
    tagName: string
  ): Promise<GitLabPipeline | null> {
    if (!this.api) throw new Error("GitLab service not configured");

    try {
      const response = await this.api.get<GitLabPipeline[]>(
        `/projects/${projectId}/pipelines`,
        { params: { ref: tagName, per_page: 1 } }
      );
      return response.data.length > 0 ? response.data[0] : null;
    } catch {
      return null;
    }
  }

  async getPipeline(
    projectId: number,
    pipelineId: number
  ): Promise<GitLabPipeline | null> {
    if (!this.api) throw new Error("GitLab service not configured");

    try {
      const response = await this.api.get<GitLabPipeline>(
        `/projects/${projectId}/pipelines/${pipelineId}`
      );
      return response.data;
    } catch {
      return null;
    }
  }

  async getMergeRequest(
    projectId: number,
    mrIid: number
  ): Promise<GitLabMergeRequest | null> {
    if (!this.api) throw new Error("GitLab service not configured");

    try {
      const response = await this.api.get<GitLabMergeRequest>(
        `/projects/${projectId}/merge_requests/${mrIid}`
      );
      return response.data;
    } catch {
      return null;
    }
  }

  async findExistingMergeRequest(
    projectId: number,
    sourceBranch: string,
    targetBranch: string
  ): Promise<GitLabMergeRequest | null> {
    if (!this.api) throw new Error("GitLab service not configured");

    try {
      const response = await this.api.get<GitLabMergeRequest[]>(
        `/projects/${projectId}/merge_requests`,
        {
          params: {
            source_branch: sourceBranch,
            target_branch: targetBranch,
            state: "opened",
            per_page: 1,
          },
        }
      );
      return response.data.length > 0 ? response.data[0] : null;
    } catch {
      return null;
    }
  }

  async createMergeRequest(
    projectId: number,
    sourceBranch: string,
    targetBranch: string,
    title: string,
    description?: string
  ): Promise<GitLabMergeRequest> {
    if (!this.api) throw new Error("GitLab service not configured");

    const response = await this.api.post<GitLabMergeRequest>(
      `/projects/${projectId}/merge_requests`,
      {
        source_branch: sourceBranch,
        target_branch: targetBranch,
        title,
        description: description || "",
        remove_source_branch: true,
      }
    );

    return response.data;
  }

  async createReleaseWithMonitoring(
    project: GitLabProject,
    releaseName: string,
    description?: string
  ): Promise<ReleaseResult> {
    try {
      // Check if UAT branch exists
      const uatBranch = await this.getBranchCommit(project.id, "uat");

      if (uatBranch) {
        // UAT branch exists - check if there's already an open MR from UAT to default branch
        const existingMR = await this.findExistingMergeRequest(
          project.id,
          "uat",
          project.default_branch
        );

        if (existingMR) {
          // Use the existing MR
          return {
            project,
            success: true,
            releaseType: "merge_request",
            mergeRequestIid: existingMR.iid,
          };
        }

        // No existing MR - create a new merge request from UAT to default branch
        const mr = await this.createMergeRequest(
          project.id,
          "uat",
          project.default_branch,
          `Release ${releaseName}`,
          description ||
            `Release ${releaseName} - merging UAT to ${project.default_branch}`
        );

        return {
          project,
          success: true,
          releaseType: "merge_request",
          mergeRequestIid: mr.iid,
        };
      } else {
        // No UAT branch - create tag on default branch (original behavior)
        const tag = await this.createTag(
          project.id,
          releaseName,
          project.default_branch,
          `Tag for release ${releaseName}`
        );

        const release = await this.createRelease(
          project.id,
          releaseName,
          releaseName,
          description || `Release ${releaseName} created via Release Creator`
        );

        return {
          project,
          success: true,
          tag,
          release,
          releaseType: "tag",
        };
      }
    } catch (error: any) {
      let errorMessage =
        error.response?.data?.message || error.message || "Unknown error";

      // Make error messages more user-friendly
      if (
        errorMessage.includes("Target") &&
        errorMessage.includes("is invalid")
      ) {
        errorMessage = `Branch "${project.default_branch}" not found - repository may be empty`;
      } else if (errorMessage.includes("already exists")) {
        return {
          project,
          success: false,
          error: `Tag "${releaseName}" already exists`,
          tagExists: true,
          releaseType: "tag",
        };
      }

      return {
        project,
        success: false,
        error: errorMessage,
      };
    }
  }

  async validateConnection(): Promise<boolean> {
    if (!this.api) return false;

    try {
      await this.api.get("/user");
      return true;
    } catch {
      return false;
    }
  }

  async getCurrentUser(): Promise<{
    username: string;
    name: string;
    avatar_url: string;
  }> {
    if (!this.api) throw new Error("GitLab service not configured");

    const response = await this.api.get("/user");
    return response.data;
  }

  /**
   * Search for projects that have commits/MRs containing the given issue key
   * This is used to find affected projects from Jira issue keys
   */
  async findProjectsByIssueKey(issueKey: string): Promise<number[]> {
    if (!this.api) throw new Error("GitLab service not configured");

    const projectIds = new Set<number>();

    try {
      // Search for commits containing the issue key in the group
      const commitsResponse = await this.api.get("/groups/kannact/search", {
        params: {
          scope: "commits",
          search: issueKey,
        },
      });

      console.log(`[GitLab Search] Found ${commitsResponse.data.length} commits for ${issueKey}`);

      for (const commit of commitsResponse.data) {
        if (commit.project_id) {
          projectIds.add(commit.project_id);
          console.log(`[GitLab Search] Commit in project ${commit.project_id}: ${commit.message?.substring(0, 50)}`);
        }
      }
    } catch (error: any) {
      console.error(`[GitLab Search] Error searching commits for ${issueKey}:`, error.message);
    }

    try {
      // Also search for merge requests containing the issue key
      const mrsResponse = await this.api.get("/groups/kannact/search", {
        params: {
          scope: "merge_requests",
          search: issueKey,
        },
      });

      console.log(`[GitLab Search] Found ${mrsResponse.data.length} MRs for ${issueKey}`);

      for (const mr of mrsResponse.data) {
        if (mr.project_id) {
          projectIds.add(mr.project_id);
          console.log(`[GitLab Search] MR in project ${mr.project_id}: ${mr.title?.substring(0, 50)}`);
        }
      }
    } catch (error: any) {
      console.error(`[GitLab Search] Error searching MRs for ${issueKey}:`, error.message);
    }

    return Array.from(projectIds);
  }

  /**
   * Find all projects affected by a list of Jira issue keys
   */
  async findProjectsForIssueKeys(issueKeys: string[]): Promise<number[]> {
    const allProjectIds = new Set<number>();

    console.log(`[GitLab Search] Searching for ${issueKeys.length} issue keys...`);

    // Search for each issue key (in parallel with rate limiting)
    const results = await Promise.all(
      issueKeys.map(async (key) => {
        try {
          return await this.findProjectsByIssueKey(key);
        } catch {
          return [];
        }
      })
    );

    for (const projectIds of results) {
      for (const id of projectIds) {
        allProjectIds.add(id);
      }
    }

    const ids = Array.from(allProjectIds);
    console.log(`[GitLab Search] Total unique projects found: ${ids.length}`, ids);
    return ids;
  }
}

export const gitlabService = new GitLabService();
